import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AnimationDuration, getAnimationAppearDisappear } from '@examdojo/animation';
import { LoaderComponent } from '@examdojo/core/loader';
import { combineLatest, map, of, switchMap, timer } from 'rxjs';

export const DEFAULT_SPINNER_DELAY_MS = 100;

@Component({
  selector: 'dojo-app-loading-overlay',
  imports: [LoaderComponent],
  template: `
    <div
      class="fullscreen-loader absolute z-[1000000] flex h-full w-full items-center justify-center"
      [class.visible]="show()"
    >
      @if (show()) {
        @defer (when showSpinner()) {
          <dojo-loader [centered]="true" @appearDisappear />
        }
      }
    </div>
  `,
  styles: `
    .fullscreen-loader {
      background-color: rgba(255, 255, 255, 0);
      opacity: 0;
      pointer-events: none;
      transition:
        background-color 0.25s ease-in-out,
        opacity 0.5s ease-in-out;

      &.visible {
        background-color: rgba(255, 255, 255, 0.7);
        opacity: 1;
        pointer-events: auto;
      }
    }

    :host-context(.dark) .fullscreen-loader.visible {
      background-color: rgba(25, 30, 41, 0.7);
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [getAnimationAppearDisappear({ enter: AnimationDuration.Long })],
})
export class AppLoadingOverlayComponent {
  readonly show = input.required<boolean>();

  readonly spinnerDelayMs = input(DEFAULT_SPINNER_DELAY_MS);

  // `@defer (on timer(…))` parses its duration at compile time, so a bound delay has to be
  // timed here. A delay change after the spinner is up leaves it up — `@defer` triggers once.
  readonly showSpinner = toSignal(
    combineLatest([toObservable(this.show), toObservable(this.spinnerDelayMs)]).pipe(
      switchMap(([show, delayMs]) => (show ? timer(delayMs).pipe(map(() => true)) : of(false))),
    ),
    { initialValue: false },
  );
}
